import {
  getLpInsightsMockHandler,
  lpInsightsDealFixtures,
} from "@vega-api/custom/msw/handlers/co-invest/lpInsightsMockHandler";
import { messagingHandlers } from "@vega-api/custom/msw/handlers/co-invest/messagingHandlers";
import { getCreateDataRoomNodeFileMockHandler } from "@vega-api/generated/coinvest-deal-data-room-files-gp/coinvest-deal-data-room-files-gp.msw";
import { getGetDataRoomNodesMockHandler } from "@vega-api/generated/coinvest-deal-data-room-nodes/coinvest-deal-data-room-nodes.msw";
import { getGetDataRoomNodesResponseMock } from "@vega-api/generated/coinvest-deal-data-room-nodes/coinvest-deal-data-room-nodes-mocks.msw";
import { getGetGpRejectReasonsMockHandler } from "@vega-api/generated/coinvest-deal-opportunity-gp-actions/coinvest-deal-opportunity-gp-actions.msw";
import { getGetNrlItemsMockHandler } from "@vega-api/generated/coinvest-deal-opportunity-nrl/coinvest-deal-opportunity-nrl.msw";
import {
  getGetAvailableRolesMockHandler,
  getGetDealProspectsPermissionViewMockHandler,
  getGetLimitedPartnerUsersPermissionViewMockHandler,
  getGetLimitedPartnerUsersPermissionViewV2MockHandler,
} from "@vega-api/generated/coinvest-deal-permission/coinvest-deal-permission.msw";
import { getGetDealOpportunityReportingMetricsMockHandler } from "@vega-api/generated/coinvest-deal-report-lp/coinvest-deal-report-lp.msw";
import { getGetDealOpportunityReportingFinancialReturnsMockHandler } from "@vega-api/generated/coinvest-deal-reporting-financial-returns-lp/coinvest-deal-reporting-financial-returns-lp.msw";
import {
  getDeleteLimitedPartnerFromShortlistMockHandler,
  getGetDealShortlistMockHandler,
} from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist.msw";
import {
  getCancelDealShortlistTeaserEmailScheduleV2MockHandler,
  getCreateDealShortlistTeaserEmailPresetV2MockHandler,
  getGetDealTeaserEmailByIdV2MockHandler,
  getGetDealTeaserEmailsSummaryV2MockHandler,
  getGetDealTeaserEmailsV2MockHandler,
  getGetDealTeaserEmailTemplateV2MockHandler,
  getPreviewDealTeaserEmailV2MockHandler,
  getScheduleDealShortlistTeaserEmailV2MockHandler,
  getUpdateDealTeaserEmailTemplateV2MockHandler,
} from "@vega-api/generated/coinvest-deal-teaser-email-v2/coinvest-deal-teaser-email-v2.msw";
import {
  getGetDealTrackingForGpMockHandler,
  getUpdateDealOpportunityTrackingMockHandler,
} from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking.msw";
import { getGetDealTrackingForGpResponseMock } from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking-mocks.msw";
import { getGetCoInvestGpPermissionsMockHandler } from "@vega-api/generated/coinvest-gp-permissions/coinvest-gp-permissions.msw";
import { getGetCoInvestLpPermissionsMockHandler } from "@vega-api/generated/coinvest-lp-permissions/coinvest-lp-permissions.msw";
import { getGetDealGeneralPartnerViewV3MockHandler } from "@vega-api/generated/deal/deal.msw";
import { getGetDealOpportunityDataPointsMockHandler } from "@vega-api/generated/deal-opportunity-data-points/deal-opportunity-data-points.msw";
import {
  getGetDealOpportunitiesForLimitedPartnerV2MockHandler,
  getGetDealOpportunityLimitedPartnerViewV3MockHandler,
} from "@vega-api/generated/deal-opportunity-lp-view/deal-opportunity-lp-view.msw";
import {
  getGetDealOpportunitiesForLimitedPartnerV2ResponseMock,
  getGetDealOpportunityLimitedPartnerViewV3ResponseMock,
} from "@vega-api/generated/deal-opportunity-lp-view/deal-opportunity-lp-view-mocks.msw";
import { getListNotificationsMockHandler } from "@vega-api/generated/notifications-v1/notifications-v1.msw";
import { CoinvestDocumentType } from "@vega-api/generated/types/coinvestDocumentType";
import { DealOpportunityStatusEnum } from "@vega-api/generated/types/dealOpportunityStatusEnum";
import { type DealShortlistItemResponse } from "@vega-api/generated/types/dealShortlistItemResponse";
import { DealStatusEnum } from "@vega-api/generated/types/dealStatusEnum";
import { type DealTeaserEmailTemplate } from "@vega-api/generated/types/dealTeaserEmailTemplate";
import { NotificationContentType } from "@vega-api/generated/types/notificationContentType";
import { type NotificationResponse } from "@vega-api/generated/types/notificationResponse";
import { type ScheduleTeaserEmailRequest } from "@vega-api/generated/types/scheduleTeaserEmailRequest";
import { type TeaserEmail } from "@vega-api/generated/types/teaserEmail";
import { type TeaserEmailDocumentView } from "@vega-api/generated/types/teaserEmailDocumentView";
import { TeaserEmailEmailRecipientType } from "@vega-api/generated/types/teaserEmailEmailRecipientType";
import { TeaserEmailStatus } from "@vega-api/generated/types/teaserEmailStatus";
import { TeaserEmailUserRecipientType } from "@vega-api/generated/types/teaserEmailUserRecipientType";
import { type UpdateDealTeaserEmailTemplateRequest } from "@vega-api/generated/types/updateDealTeaserEmailTemplateRequest";

import { fillPreviewTeaserEmailTemplate } from "./previewTeaserEmailTemplate";

const DATA_ROOM_ID = "423e4567-e89b-12d3-a456-426614170002";
const DEAL_ID = "123e4567-e89b-12d3-a456-426614170001";
const DEAL_OPPORTUNITY_ID = "223e4567-e89b-12d3-a456-426614170003";

// Align tracking and data-room permissions for NRL "Track new" search
const trackingResponse = getGetDealTrackingForGpResponseMock();
const trackingRows = trackingResponse.data?.limitedPartnerBreakdown ?? [];

const trackingDealOpportunityPermissions = trackingRows.map((row) => ({
  dealOpportunityId: row.dealOpportunityId,
}));

const dataRoomResponse = getGetDataRoomNodesResponseMock();

dataRoomResponse.data.nodes.forEach((node) => {
  if (!node.isFolder) {
    node.dealOpportunityPermissions = trackingDealOpportunityPermissions;
  }
});

const baseApprovedItem: DealShortlistItemResponse = {
  id: "approved-base",
  dealId: DEAL_ID,
  dealOpportunityId: "opportunity-approved-base",
  dealLink: "https://example.com/deal",
  limitedPartner: { id: "lp-approved-base", name: "Approved LP" },
  keyContact: {
    id: "contact-base",
    fullName: { firstName: "Alex", lastName: "Green" },
    email: "alex@example.com",
  },
  relationship: {
    id: "rel-base",
    fullName: { firstName: "Morgan", lastName: "West" },
    email: "morgan@example.com",
  },
  tier: "1",
  isDealSent: false,
  canSendDeal: true,
  canRemoveFromShortlist: true,
  wasAddedByUser: true,
  isPermissionLocked: false,
  isApproved: true,
};

// One item per DraftStatusButton variant; the no-email item also stands in for
// the CANCELLED case, which renders the same neutral variant.
const approvedShortlistItems: DealShortlistItemResponse[] = [
  {
    ...baseApprovedItem,
    id: "approved-sent",
    dealOpportunityId: "opportunity-sent",
    limitedPartner: { id: "lp-sent", name: "Sent LP" },
    teaserEmail: { emailId: "email-sent", status: TeaserEmailStatus.SENT },
  },
  {
    ...baseApprovedItem,
    id: "approved-scheduled",
    dealOpportunityId: "opportunity-scheduled",
    limitedPartner: { id: "lp-scheduled", name: "Scheduled LP" },
    teaserEmail: {
      emailId: "email-scheduled",
      status: TeaserEmailStatus.SCHEDULED,
      scheduledAt: "2030-01-01T10:00:00Z",
    },
  },
  {
    ...baseApprovedItem,
    id: "approved-draft",
    dealOpportunityId: "opportunity-draft",
    limitedPartner: { id: "lp-draft", name: "Draft LP" },
    teaserEmail: { emailId: "email-draft", status: TeaserEmailStatus.DRAFT },
  },
  {
    ...baseApprovedItem,
    id: "approved-no-email",
    dealOpportunityId: "opportunity-no-email",
    limitedPartner: { id: "lp-no-email", name: "No Email LP" },
  },
  {
    ...baseApprovedItem,
    id: "approved-no-access",
    dealOpportunityId: "opportunity-no-access",
    limitedPartner: { id: "lp-no-access", name: "No Access LP" },
  },
];

const memberCountByLimitedPartnerId: Record<string, number> = {
  "lp-no-access": 0,
};

const teaserEmailRecipients = [
  {
    type: TeaserEmailUserRecipientType.USER,
    teaserEmailUserRecipientDiscriminator: "TRUE" as const,
    userId: "lp-user-1",
    fullName: { firstName: "Dana", lastName: "Lopez" },
    email: "dana.lopez@example.com",
  },
];

const teaserEmailSender = {
  userId: "gp-user-1",
  fullName: { firstName: "Morgan", lastName: "West" },
  email: "morgan.west@example.com",
};

const teaserEmailDocumentView: TeaserEmailDocumentView = {
  ndaDocument: {
    documentId: "nda-doc-1",
    nameOfDocument: "NDA.pdf",
    typeOfDocument: CoinvestDocumentType.PDF,
  },
  teaserDocument: {
    documentId: "teaser-doc-1",
    nameOfDocument: "Teaser.pdf",
    typeOfDocument: CoinvestDocumentType.PDF,
  },
};

const teaserEmails: TeaserEmail[] = [
  {
    id: "email-scheduled",
    dealShortlistItemId: "approved-scheduled",
    status: TeaserEmailStatus.SCHEDULED,
    recipients: teaserEmailRecipients,
    ccRecipients: [],
    subject: "Co-investment opportunity — scheduled",
    body: "<p>Scheduled draft body.</p>",
    scheduledAt: "2030-01-01T10:00:00Z",
    attachNda: true,
    attachTeaser: true,
    documentView: teaserEmailDocumentView,
  },
  {
    id: "email-draft",
    dealShortlistItemId: "approved-draft",
    status: TeaserEmailStatus.DRAFT,
    recipients: teaserEmailRecipients,
    ccRecipients: [
      {
        type: TeaserEmailEmailRecipientType.EMAIL,
        teaserEmailEmailRecipientDiscriminator: "TRUE",
        email: "alex.kim@example.com",
      },
      {
        type: TeaserEmailEmailRecipientType.EMAIL,
        teaserEmailEmailRecipientDiscriminator: "TRUE",
        email: "sam.cole@example.com",
      },
    ],
    subject: "Co-investment opportunity — draft",
    body: "<p>Unsent draft body.</p>",
    attachNda: true,
    attachTeaser: true,
    documentView: teaserEmailDocumentView,
  },
  {
    id: "email-no-recipients",
    dealShortlistItemId: "approved-no-email",
    status: TeaserEmailStatus.DRAFT,
    recipients: [],
    ccRecipients: [],
    subject: "Co-investment opportunity — add a recipient",
    body: "<p>This draft still needs a recipient.</p>",
    attachNda: true,
    attachTeaser: true,
    documentView: teaserEmailDocumentView,
  },
];

const draftEmailsById: Record<string, TeaserEmail> = Object.fromEntries(
  teaserEmails.map((email) => [email.id, email]),
);

const dealEmailTemplate: DealTeaserEmailTemplate = {
  subject: "[[GP_NAME]] — [[DEAL_NAME]] co-investment opportunity",
  body: "Dear [[LP_NAME]] team,<br><br>[[GP_NAME]] is pleased to share [[DEAL_NAME]] with you.<br><br>See the attached materials for more information.<br><br>Best regards,<br>[[GP_USER_NAME]]",
  attachNda: true,
  attachTeaser: true,
  placeholderVariables: [
    {
      name: "GP name",
      token: "[[GP_NAME]]",
      description: "The name of the general partner",
    },
    {
      name: "LP name",
      token: "[[LP_NAME]]",
      description: "The name of the limited partner receiving the email",
    },
    {
      name: "Deal name",
      token: "[[DEAL_NAME]]",
      description: "The name of the deal",
    },
    {
      name: "GP user name",
      token: "[[GP_USER_NAME]]",
      description: "The name of the GP user sending the email",
    },
  ],
};

// Every LP→GP co-invest notification type, one unread notification each, all
// scoped to DEAL_ID so the deal-bell and deal filter surface them.
const lpToGpNotificationTypes: {
  type: NotificationContentType;
  title: string;
  body: string;
}[] = [
  {
    type: NotificationContentType.COINVEST_NOTIFICATION_NDA_SIGNED,
    title: "NDA signed",
    body: "Approved LP signed the NDA for Series A Investment.",
  },
  {
    type: NotificationContentType.COINVEST_NOTIFICATION_ORDER_SUBMITTED,
    title: "Order submitted",
    body: "Approved LP submitted an order for Series A Investment.",
  },
  {
    type: NotificationContentType.COINVEST_NOTIFICATION_ALLOCATION_ACCEPTED,
    title: "Allocation accepted",
    body: "Approved LP accepted their allocation for Series A Investment.",
  },
  {
    type: NotificationContentType.COINVEST_NOTIFICATION_ALLOCATION_REJECTED,
    title: "Allocation rejected",
    body: "Approved LP rejected their allocation for Series A Investment.",
  },
  {
    type: NotificationContentType.COINVEST_NOTIFICATION_DATA_ROOM_FIRST_ACCESS,
    title: "Data room first access",
    body: "Approved LP accessed the data room for Series A Investment.",
  },
];

// Every GP→LP co-invest notification type, one unread notification each, all
// scoped to DEAL_OPPORTUNITY_ID so the deal-bell and deal filter surface them.
const gpToLpNotificationTypes: {
  type: NotificationContentType;
  title: string;
  body: string;
}[] = [
  {
    type: NotificationContentType.COINVEST_NOTIFICATION_ALLOCATION_SENT,
    title: "Allocation sent",
    body: "The GP sent you an allocation for Series A Investment.",
  },
  {
    type: NotificationContentType.COINVEST_NOTIFICATION_NDA_SENT,
    title: "NDA sent",
    body: "The GP sent you an NDA to sign for Series A Investment.",
  },
  {
    type: NotificationContentType.COINVEST_NOTIFICATION_NDA_CONFIRMED,
    title: "NDA confirmed",
    body: "Your NDA for Series A Investment has been confirmed.",
  },
  {
    type: NotificationContentType.COINVEST_NOTIFICATION_ORDER_REJECTED,
    title: "Order rejected",
    body: "Your order for Series A Investment was rejected.",
  },
  {
    type: NotificationContentType.COINVEST_NOTIFICATION_DEAL_CLOSED,
    title: "Deal closed",
    body: "Series A Investment has closed.",
  },
  {
    type: NotificationContentType.COINVEST_NOTIFICATION_DATA_ROOM_ACCESS_GRANTED,
    title: "Data room access granted",
    body: "You've been granted data room access for Series A Investment.",
  },
  {
    type: NotificationContentType.COINVEST_NOTIFICATION_DEAL_ACCESS_GRANTED,
    title: "Deal access granted",
    body: "You've been granted access to Series A Investment.",
  },
];

const NOTIFICATION_ROUNDS = 7;

const notificationTypes = [
  ...lpToGpNotificationTypes.map((notificationType) => ({
    ...notificationType,
    targetId: DEAL_ID,
    meta: { dealId: DEAL_ID },
  })),
  ...gpToLpNotificationTypes.map((notificationType) => ({
    ...notificationType,
    targetId: DEAL_OPPORTUNITY_ID,
    meta: { dealOpportunityId: DEAL_OPPORTUNITY_ID },
  })),
];

const coinvestNotifications: NotificationResponse[] = Array.from(
  { length: NOTIFICATION_ROUNDS },
  (_, round) =>
    notificationTypes.map(
      ({ type, title, body, targetId, meta }, typeIndex) => {
        const index = round * notificationTypes.length + typeIndex;
        const day = String((index % 28) + 1).padStart(2, "0");

        return {
          id: `coinvest-notification-${index}`,
          targetId,
          type,
          title: `${title} #${round + 1}`,
          body,
          publisher: "coinvest-deals",
          meta,
          createdAt: `2026-06-${day}T10:00:00Z`,
        };
      },
    ),
).flat();

const CLOSED_LP_DEAL_OPPORTUNITY_ID = "closed-committed-opportunity";

const CLOSED_LP_DEAL_NAME = "Closed Committed Deal (report demo)";

const lpDealListMock = getGetDealOpportunitiesForLimitedPartnerV2ResponseMock();
const closedListItem =
  getGetDealOpportunitiesForLimitedPartnerV2ResponseMock().data[0];
closedListItem.deal.name = CLOSED_LP_DEAL_NAME;
closedListItem.deal.closingDate = "2026-04-30";
closedListItem.dealOpportunity.id = CLOSED_LP_DEAL_OPPORTUNITY_ID;
closedListItem.dealOpportunity.status = DealOpportunityStatusEnum.COMMITTED;
closedListItem.dealOpportunity.isClosed = true;
lpDealListMock.data.unshift(closedListItem);

export const coinvestHandlers = [
  ...messagingHandlers,
  getGetDealOpportunitiesForLimitedPartnerV2MockHandler(lpDealListMock),
  getGetDealGeneralPartnerViewV3MockHandler({
    data: {
      deal: {
        id: DEAL_ID,
        name: "Series A Investment",
        generalPartnerId: "123e4567-e89b-12d3-a456-426614170002",
        createdAt: "2024-11-01T10:00:00Z",
        vegaDataRooms: [{ id: DATA_ROOM_ID, name: "Project Dragon Data Room" }],
      },
      teaser: {
        id: "123e4567-e89b-12d3-a456-426614174111",
        dealId: DEAL_ID,
        documentMetaData: {
          documentId: "123e4567-e89b-12d3-a456-426614174111",
          nameOfDocument: "teaser.pdf",
          typeOfDocument: CoinvestDocumentType.PDF,
        },
      },
    },
  }),
  getGetDealTrackingForGpMockHandler(),
  getGetDataRoomNodesMockHandler(() => dataRoomResponse),
  getGetCoInvestGpPermissionsMockHandler(),
  getGetDealOpportunityReportingMetricsMockHandler(),
  getGetDealOpportunityReportingFinancialReturnsMockHandler(),
  getLpInsightsMockHandler(lpInsightsDealFixtures.fullSection),
  getGetDealOpportunityLimitedPartnerViewV3MockHandler((info) => {
    const mock = getGetDealOpportunityLimitedPartnerViewV3ResponseMock();

    if (info.params.id === CLOSED_LP_DEAL_OPPORTUNITY_ID && mock.data) {
      mock.data.deal.name = CLOSED_LP_DEAL_NAME;
      mock.data.deal.status = DealStatusEnum.CLOSED;
      mock.data.dealOpportunity.status = DealOpportunityStatusEnum.COMMITTED;
    }

    return mock;
  }),
  getCreateDataRoomNodeFileMockHandler(),
  getUpdateDealOpportunityTrackingMockHandler(),
  getGetNrlItemsMockHandler(),
  getGetDealShortlistMockHandler(({ request }) => {
    const isApproved =
      new URL(request.url).searchParams.get("isApproved") === "true";
    const dealShortlistItems = isApproved ? approvedShortlistItems : [];

    return {
      data: { dealId: DEAL_ID, dealShortlistItems, canAddShortlistItems: true },
      meta: {
        page: 1,
        pageSize: 10,
        count: dealShortlistItems.length,
        totalPages: 1,
        hasNextPage: false,
      },
    };
  }),
  getDeleteLimitedPartnerFromShortlistMockHandler(),
  getCreateDealShortlistTeaserEmailPresetV2MockHandler({
    data: {
      dealShortlistItemId: "approved-no-email",
      recipients: teaserEmailRecipients,
      ccRecipients: [],
      sender: teaserEmailSender,
      subject: "Co-investment opportunity",
      body: "<p>Prefilled teaser email body.</p>",
      attachNda: true,
      attachTeaser: true,
      documentView: teaserEmailDocumentView,
    },
  }),
  getGetDealTeaserEmailsSummaryV2MockHandler({
    data: {
      draftCount: teaserEmails.filter(
        (email) => email.status === TeaserEmailStatus.DRAFT,
      ).length,
      scheduledCount: teaserEmails.filter(
        (email) => email.status === TeaserEmailStatus.SCHEDULED,
      ).length,
    },
  }),
  getGetDealTeaserEmailByIdV2MockHandler(({ params }) => {
    const emailId = String(params.emailId);

    return { data: draftEmailsById[emailId] ?? draftEmailsById["email-draft"] };
  }),
  getPreviewDealTeaserEmailV2MockHandler(async ({ request }) => {
    const { body } = (await request.json()) as { body?: string };
    const dealId =
      new URL(request.url).pathname.match(/\/deals\/([^/]+)\//)?.[1] ?? "";

    return {
      data: {
        html: fillPreviewTeaserEmailTemplate({
          body: body ?? "",
          dealUrl: `https://coinvest.vega-test.link/co-investments/${dealId}`,
        }),
      },
    };
  }),
  getGetDealTeaserEmailTemplateV2MockHandler(() => ({
    data: dealEmailTemplate,
  })),
  getUpdateDealTeaserEmailTemplateV2MockHandler(async ({ request }) => {
    const body = (await request.json()) as UpdateDealTeaserEmailTemplateRequest;
    Object.assign(dealEmailTemplate, body);

    return { data: dealEmailTemplate };
  }),
  getGetDealTeaserEmailsV2MockHandler(({ request }) => {
    const requestedStatuses = new URL(request.url).searchParams
      .getAll("statuses")
      .flatMap((value) => value.split(","));
    const data =
      requestedStatuses.length > 0
        ? teaserEmails.filter((email) =>
            requestedStatuses.includes(email.status),
          )
        : teaserEmails;

    return { data };
  }),
  // Stateful so the modal flips between locked/editable in place: cancel-schedule
  // moves the email to DRAFT, schedule moves it (back) to SCHEDULED.
  getCancelDealShortlistTeaserEmailScheduleV2MockHandler(({ params }) => {
    const email =
      draftEmailsById[String(params.emailId)] ?? draftEmailsById["email-draft"];
    email.status = TeaserEmailStatus.DRAFT;
    email.scheduledAt = undefined;

    return { data: email };
  }),
  getScheduleDealShortlistTeaserEmailV2MockHandler(
    async ({ params, request }) => {
      const { scheduledAt } =
        (await request.json()) as ScheduleTeaserEmailRequest;
      const email =
        draftEmailsById[String(params.emailId)] ??
        draftEmailsById["email-draft"];
      email.status = TeaserEmailStatus.SCHEDULED;
      email.scheduledAt = scheduledAt;

      return { data: email };
    },
  ),
  getGetCoInvestLpPermissionsMockHandler(),
  getGetDealProspectsPermissionViewMockHandler({
    data: {
      limitedPartners: approvedShortlistItems.map((item, index) => ({
        limitedPartner: item.limitedPartner,
        memberCount:
          memberCountByLimitedPartnerId[item.limitedPartner.id] ?? index + 1,
      })),
    },
  }),
  getGetLimitedPartnerUsersPermissionViewMockHandler(),
  getGetLimitedPartnerUsersPermissionViewV2MockHandler(),
  getGetAvailableRolesMockHandler(),
  getGetGpRejectReasonsMockHandler(),
  getGetDealOpportunityDataPointsMockHandler(),
  getListNotificationsMockHandler(({ request }) => {
    const params = new URL(request.url).searchParams;
    const page = Number(params.get("page") ?? 1);
    const pageSize = Number(
      params.get("pageSize") ?? coinvestNotifications.length,
    );
    const start = (page - 1) * pageSize;
    const pageItems = coinvestNotifications.slice(start, start + pageSize);

    return {
      data: pageItems,
      meta: {
        page,
        pageSize,
        count: coinvestNotifications.length,
        totalPages: Math.ceil(coinvestNotifications.length / pageSize),
        hasNextPage: start + pageSize < coinvestNotifications.length,
      },
    };
  }),
];
